import React, { useEffect, useState } from 'react';
import { Button, ColorPicker, Form, Input, InputNumber, Popconfirm, Space, Table, Typography } from 'antd';
import { Tool, ToolService } from '../../services/Tool';
import { getCurrentUser } from '../../services/User';


interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'color' | 'text' | 'number';
  record: Tool;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === 'color' ? <ColorPicker/> : <Input />;
  const displayNode = inputType === 'color' ? <div style={{backgroundColor: record.color, borderRadius: '5px', color: record.color}}> color </div> : children;
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
          getValueFromEvent={(value) => {
            if(inputType === 'color')
              return "#" + value.toHex();
            else
              return value.currentTarget.value
          }}
        >
          {inputNode}
        </Form.Item>
      ) : (
        displayNode
      )}
    </td>
  );
};

const ManageTools: React.FC = () => {
  const [form] = Form.useForm();
  const [tools, setTools] = useState<Tool[]>([]);
  const [editingKey, setEditingKey] = useState('');
  const user = getCurrentUser();

  useEffect(() => {
    const getTools = async () => {
      const {data } = await ToolService.getAll(user.country as string);
      setTools(data);
    }

    getTools();
  },[])

  const isEditing = (record: Tool) => record._id === editingKey;

  const edit = (record: Tool) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record._id!);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const deleteTool = async (id: string) => {
      const newData = [...tools];
      const index = newData.findIndex((item: Tool) => id === item._id);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1 );
        setTools(newData);
        setEditingKey('');
        await ToolService.delete(item._id!)
      }
  };

  const save = async (id: string) => {
    try {
      const row = (await form.validateFields()) as Tool;

      const newData = [...tools];
      const index = newData.findIndex((item: Tool) => id === item._id!);
      if (index > -1) {
        const item = newData[index];
        const {data} = await ToolService.update({...item, ...row});
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setTools(newData);
       
        setEditingKey('');
      } else {
        newData.push(row);
        setTools(newData);
        setEditingKey('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const columns = [
    {
      title: 'Application/Tool',
      dataIndex: 'title',
      width: '50%',
      editable: true,
    },
    {
      title: 'Color',
      dataIndex: 'color',
      width: '30px',
      editable: true,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_: any, record: Tool) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link onClick={() => save(record._id!)} style={{ marginRight: 8 }}>
              Save
            </Typography.Link>
            <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
              <a>Cancel</a>
            </Popconfirm>
          </span>
        ) : (
          <Space>
            <Typography.Link disabled={editingKey !== ''} onClick={() => edit(record)}>
              Edit
            </Typography.Link>
            <Popconfirm title="Sure to delete?" onConfirm={() => deleteTool(record._id!)}>
              <a>Delete</a>
            </Popconfirm>
         </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Tool) => ({
        record,
        inputType: col.dataIndex === 'color' ? 'color' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const addTool = async () => {
    const {data} = await ToolService.create({
     title: '',
     color: '#006eb5',
     country: user.country!
    });
   
    setTools([ 
      data,
      ...tools,
    ])
    form.setFieldsValue({ ...data });
    setEditingKey(data._id);
  }

  return (
    <>
      <Button onClick={addTool} type='primary' style={{marginBottom:16}} disabled={editingKey !== ''}>+ Add Tool</Button>
      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={tools}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={false}
          style={{minWidth:'500px'}}
          rowKey='_id'
        />
      </Form>
    </>
  );
};

export default ManageTools;